import { useCallback, useEffect, useRef, useState } from "react";

const ANNOUNCEMENT_DURATION_MS = 350;

/**
 * Intended to be used to show an screen-reader only aria-live region.
 * The consumer calls an announce method to set the isAnnouncing flag to true,
 * which should be used by the consumer to play a short-lived aria-live message.
 * Automatically clears out after duration set in ANNOUNCEMENT_DURATION_MS.
 * Best practice is to place this away from where the announcement is triggered
 * so the live region does not accidentally get navigated to.
 *
 * @returns
 * - `isAnnouncing` — whether the region should currently hold its message.
 * - `announce` — shows the message and schedules it to clear.
 * - `cancel` — clears the message immediately.
 */
export default function useLiveAnnouncement(): [
  isAnnouncing: boolean,
  announce: () => void,
  cancel: () => void,
] {
  const [isAnnouncing, setIsAnnouncing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const announce = useCallback(() => {
    setIsAnnouncing(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(
      () => setIsAnnouncing(false),
      ANNOUNCEMENT_DURATION_MS,
    );
  }, []);

  const cancel = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsAnnouncing(false);
  }, []);

  return [isAnnouncing, announce, cancel];
}
